import { type ComponentProps, type RefObject, type KeyboardEvent } from 'react';
import set from 'lodash/set';
import isEqual from 'lodash/isEqual';
import { get } from 'lodash';
import { animateScroll } from 'react-scroll';
import { FormattedMessage } from 'react-intl';

type TMessageValues = ComponentProps<typeof FormattedMessage>['values'];

interface ICheckboxesGroupFormField {
  name: string;
  defaultValue?: unknown;
}

interface ICheckboxesGroupItem {
  name: string;
  autoselect?: boolean;
  form: ICheckboxesGroupFormField[];
}

interface IWizardStepField {
  name?: string;
  type?: string;
  value?: unknown;
  defaultValue?: unknown;
  /** The nested fields of a `group` field. */
  components?: IWizardStepField[];
  /** The checkboxes of a `checkboxes_group` field. */
  fields?: ICheckboxesGroupItem[];
}

export interface IWizardStep {
  fields: IWizardStepField[];
}

interface IFeatureValue {
  name: string;
  value: boolean;
}

interface IGetIndexElementFromArrayArgs<T> {
  array: T[];
  searchedValue: T;
}

/**
 * A yup validation message that renders a translation. yup calls a function message when the
 * test fails and keeps what it returns (the element) as the error; formik passes it on to the
 * inputs, which render it.
 */
export function __(id: string, values?: TMessageValues) {
  return () => <FormattedMessage {...{ id, values }} />;
}

export const getInitialValuesFromSteps = (steps: IWizardStep[] | null | undefined) => {
  if (!steps && !Array.isArray(steps)) return {};
  const initialValues: Record<string, unknown> = {};
  const features: IFeatureValue[] = [];
  const getDefaultValues = (fields: IWizardStepField[] = []) => {
    fields.forEach((f) => {
      // Bug: this guard read `if (!f.name && !f.type === 'group') return;`, which never returns
      // (`!f.type` is a boolean, never 'group'), so fields without a name are processed as well.
      if (f.type === 'group') {
        getDefaultValues(f.components);
      } else if (f.type === 'text') {
        initialValues[String(f.name)] = f.value || '';
      } else if (f.type === 'switch') {
        initialValues[String(f.name)] = f.value || undefined;
      } else if (f.type === 'checkboxes_group') {
        initialValues.features = features;
        (f.fields ?? []).forEach(({ autoselect, name, form }) => {
          if (autoselect) {
            features.push({ name, value: true });
          }
          form.forEach((formField) => {
            set(initialValues, `${name}--${formField.name}`, formField.defaultValue);
          });
        });
      } else if (f.defaultValue !== undefined) {
        initialValues[String(f.name)] = f.defaultValue; // === undefined ? null : f.defaultValue;
      }
    });
  };
  steps.map((schema) => getDefaultValues(schema.fields));
  return initialValues;
};

export const getIndexElementFromArray = <T,>({
  array,
  searchedValue,
}: IGetIndexElementFromArrayArgs<T>) => array.findIndex((element) => element === searchedValue);

export const areEqualFields = (
  prevProps: object,
  nextProps: object,
  additionalKeys: string[] = [],
) => {
  const keys = ['field', 'form.errors', 'form.touched', ...additionalKeys];
  const res = keys.every((key) => isEqual(get(prevProps, key), get(nextProps, key)));
  return res;
};

function scrollToElement($targetElement: Element) {
  const coordinates = $targetElement.getBoundingClientRect();
  animateScroll.scrollTo(coordinates.top + window.pageYOffset - 300);
}

function focusInputElement($targetElement: Element) {
  const $inputElement = $targetElement.querySelector<HTMLInputElement>(
    'input[type="text"], input[type="number"]',
  );
  if ($inputElement) $inputElement.focus();
}

function getMatchingSibling($elem: Element, selector: string): Element | null {
  const $nextSibling = $elem.nextSibling;
  if (!$nextSibling) return null;
  // Bug: a text or comment node as the next sibling has no `matches`, so the walk throws; the
  // field wrappers are separated by elements only, so it does not happen in practice.
  if (!($nextSibling instanceof Element)) {
    throw new TypeError('$nextSibling.matches is not a function');
  }
  if ($nextSibling.matches(selector)) return $nextSibling;

  const $deepMatchingElem = $nextSibling.querySelector(selector);
  if ($deepMatchingElem) return $deepMatchingElem;

  return getMatchingSibling($nextSibling, selector);
}

function getMatchingParent(currentElement: Element, selector: string): Element | null {
  const { parentElement } = currentElement;
  // Bug: an element outside a form reaches the document root and throws here
  // (`parentElement.tagName` of null); the fields are always inside a form.
  if (!parentElement) {
    throw new TypeError("Cannot read properties of null (reading 'tagName')");
  }
  if (parentElement.tagName === 'FORM') return null;
  const $nextMatchingField = getMatchingSibling(parentElement, selector);
  return $nextMatchingField || getMatchingParent(parentElement, selector);
}

export function scrollToNextMatchingSibling(ref: RefObject<Element | null>) {
  const $currentElement = ref.current;
  if (!$currentElement) return;

  const selector = 'div[class*="scroll-to-field"]';
  const $nextMatchingFieldWrapper =
    getMatchingSibling($currentElement, selector) || getMatchingParent($currentElement, selector);

  let $targetElement;
  if ($nextMatchingFieldWrapper) {
    $targetElement = $nextMatchingFieldWrapper;
  } else {
    $targetElement = document.querySelector('button[type="submit"]');
  }
  if (!$targetElement) return;

  scrollToElement($targetElement);
  focusInputElement($targetElement);
}

export const onKeyDown = (keyEvent: KeyboardEvent, fieldWrapperRef: RefObject<Element | null>) => {
  if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
    keyEvent.preventDefault();
    scrollToNextMatchingSibling(fieldWrapperRef);
  }
};
